import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Icon } from '@/components/Icon/Icon';
import type { BrokerageMember, BrokerageRole } from '@/graphql/brokerages';
import { extraRoles, memberLabel, memberRoleName, sortMembers } from '@/lib/brokerages';
import { formatDay } from '@/lib/format';

import styles from './MemberList.module.css';

export interface MemberListProps {
  members: readonly BrokerageMember[];
  /** The brokerage's roles, so extra roles a member holds can be named. */
  roles?: readonly BrokerageRole[];
  meId?: string | null;
  canEdit: boolean;
  onChangeRole: (member: BrokerageMember) => void;
  onDeactivate: (member: BrokerageMember) => void;
  onReactivate: (member: BrokerageMember) => void;
}

/** Everyone in one brokerage, their role, and any extra roles they hold. */
export function MemberList({
  members,
  roles = [],
  meId,
  canEdit,
  onChangeRole,
  onDeactivate,
  onReactivate,
}: MemberListProps) {
  return (
    <ul aria-label="Members" className={styles.list}>
      {sortMembers(members).map((member) => {
        const label = memberLabel(member, meId);
        const ended = Boolean(member.deletedAt);
        const isMe = Boolean(member.personId) && member.personId === meId;
        const pending = member.role?.toUpperCase() === 'PENDING';
        return (
          <li className={`${styles.row} ${ended ? styles.ended : ''}`} key={member.id}>
            <span aria-hidden="true" className={styles.avatar}>
              <Icon name={ended ? 'eye-slash' : 'lock-key'} />
            </span>
            <span className={styles.main}>
              <span className={styles.name}>{label}</span>
              <span className={styles.meta}>
                {ended
                  ? `Removed ${formatDay(member.deletedAt)}`
                  : `Joined ${formatDay(member.createdAt)}`}
              </span>
            </span>
            <span className={styles.roles}>
              {ended ? (
                <Chip tone="bad">Removed</Chip>
              ) : (
                <Chip tone={pending ? 'warn' : 'neutral'} label="Role">
                  {memberRoleName(member.role)}
                </Chip>
              )}
              {ended
                ? null
                : extraRoles(member, roles).map((role) => (
                    <Chip key={role.id} label="Extra role" tone="good">
                      {role.name ?? 'Unnamed role'}
                    </Chip>
                  ))}
            </span>
            {canEdit && !isMe ? (
              <span className={styles.actions}>
                {ended ? (
                  <Button
                    aria-label={`Restore ${label}`}
                    onClick={() => onReactivate(member)}
                    size="sm"
                    variant="ghost"
                  >
                    Restore
                  </Button>
                ) : (
                  <>
                    <Button
                      aria-label={`Change role for ${label}`}
                      onClick={() => onChangeRole(member)}
                      size="sm"
                      variant="ghost"
                    >
                      Change role
                    </Button>
                    <Button
                      aria-label={`Remove ${label}`}
                      onClick={() => onDeactivate(member)}
                      size="sm"
                      variant="quiet"
                    >
                      Remove
                    </Button>
                  </>
                )}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
